import React, { useEffect, useState } from 'react';
import { getAllFinancialReports } from '../../lib/data/financialReportsHelper';
import { AccountEntry, Project, CategoryTemplate } from '../../types';

// --- Formatting utilities ---
function formatNumber(value: number): string {
  return value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}
function formatPercent(value: number): string {
  return (value * 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + '%';
}

// --- Table transformation logic (copied from backend, but as pure JS functions) ---
function createCompactTableFormat(accounts: any[]): string {
  if (!accounts || !Array.isArray(accounts) || accounts.length === 0) return 'No trial balance data.';
  const accountGroups = accounts.reduce((groups, account) => {
    const key = account.accountNumber;
    if (!groups[key]) {
      groups[key] = {
        num: account.accountNumber,
        name: account.name,
        cat: account.category || 'Uncat',
        data: {}
      };
    }
    groups[key].data[account.month] = (account.debit || 0) - (account.credit || 0);
    return groups;
  }, {} as Record<string, any>);
  const periods = [...new Set(accounts.map(acc => acc.month))]
    .filter((p): p is string => typeof p === 'string' && p.includes('-'))
    .sort((a, b) => {
      const parseDate = (period: string) => {
        if (typeof period !== 'string' || !period.includes('-')) return new Date('2000-01-01');
        const [month, year] = period.split('-');
        const fullYear = year && year.length === 2 ? '20' + year : year || '2000';
        return new Date(`${month || 'Jan'} 1, ${fullYear}`);
      };
      return parseDate(a).getTime() - parseDate(b).getTime();
    });
  let result = `All amounts in actual USD amounts (except percentages)\n`;
  result += `Acc#|Name|Cat|${periods.join('|')}\n`;
  Object.values(accountGroups).forEach((acc: any) => {
    const values = periods.map(p => formatNumber(acc.data[p] || 0));
    result += `${acc.num}|${acc.name.substring(0, 30)}|${acc.cat}|${values.join('|')}\n`;
  });
  return result;
}
// Helper to get year from period string (e.g., 'Jan-24' -> 2024)
function getYear(period: string): number {
  const [_, year] = period.split('-');
  return year.length === 2 ? 2000 + parseInt(year) : parseInt(year);
}
// Helper to get month index from period string (e.g., 'Jan-24' -> 0)
function getMonthIndex(period: string): number {
  const [month] = period.split('-');
  return ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'].indexOf(month);
}
// Helper to get all years in periods
function getAllYears(periods: string[]): number[] {
  return Array.from(new Set(periods.map(getYear))).sort((a, b) => a - b);
}
// Helper to get all quarters in periods
function getAllQuarters(periods: string[]): string[] {
  const quarters = periods.map(p => {
    const [month, year] = p.split('-');
    let q = '';
    if (['Jan', 'Feb', 'Mar'].includes(month)) q = `Q1-${year}`;
    else if (['Apr', 'May', 'Jun'].includes(month)) q = `Q2-${year}`;
    else if (['Jul', 'Aug', 'Sep'].includes(month)) q = `Q3-${year}`;
    else if (['Oct', 'Nov', 'Dec'].includes(month)) q = `Q4-${year}`;
    return q;
  });
  return Array.from(new Set(quarters)).sort();
}
// Helper to sum values for a set of period indices
function sumValues(values: number[], indices: number[]): number {
  return indices.reduce((sum, idx) => sum + (values[idx] || 0), 0);
}
// Enhanced table creation for P&L, BS, etc. - Period-aware version
function createEnhancedTable(report: any, reportType: string, periodType: 'monthly' | 'quarterly' | 'annual' = 'monthly'): string {
  if (!report || !report.data) return '';
  const sortPeriods = (periods: string[]) => {
    return periods
      .filter((p): p is string => typeof p === 'string' && p.includes('-'))
      .sort((a, b) => {
        const parseDate = (period: string) => {
          if (typeof period !== 'string' || !period.includes('-')) return new Date('2000-01-01');
          const [month, year] = period.split('-');
          const fullYear = year && year.length === 2 ? '20' + year : year || '2000';
          return new Date(`${month || 'Jan'} 1, ${fullYear}`);
        };
        return parseDate(a).getTime() - parseDate(b).getTime();
      });
  };
  const firstLineItem = Object.keys(report.data)[0];
  if (!firstLineItem) return '';
  const periods = sortPeriods(Object.keys(report.data[firstLineItem]));
  if (periods.length === 0) return '';
  const years = getAllYears(periods);
  
  // Period-aware LTM calculation
  let ltmPeriods: string[] = [];
  let ltmLabel = 'LTM';
  if (periodType === 'monthly') {
    ltmPeriods = periods.slice(-12); // Last 12 months
    ltmLabel = ltmPeriods.length > 0 ? `LTM ${ltmPeriods[ltmPeriods.length-1]}` : 'LTM';
  } else if (periodType === 'quarterly') {
    ltmPeriods = periods.slice(-4); // Last 4 quarters = 12 months
    ltmLabel = ltmPeriods.length > 0 ? `LTM ${ltmPeriods[ltmPeriods.length-1]}` : 'LTM';
  } else if (periodType === 'annual') {
    ltmPeriods = periods.slice(-1); // For annual data, LTM = most recent year
    ltmLabel = ltmPeriods.length > 0 ? `LTM ${ltmPeriods[ltmPeriods.length-1]}` : 'LTM';
  }

  // YTD: periods in current year (only relevant for monthly/quarterly)
  const currentYear = getYear(periods[periods.length-1]);
  const ytdPeriods = periods.filter(p => getYear(p) === currentYear);
  const ytdLabel = `YTD ${currentYear}`;
  
  // Period-aware header construction
  let result = `All amounts in actual USD amounts (except percentages)\n`;
  result += `${reportType.toUpperCase()} DATA (Table Format)\n`;
  
  if (periodType === 'annual') {
    // For annual data: only show periods and LTM (no redundant annual aggregation)
    result += `Line Item|${periods.join('|')}|${ltmLabel}\n`;
  } else if (periodType === 'quarterly') {
    // For quarterly data: show periods, annual aggregations, and LTM
    const annualLabels = years.map(y => y.toString());
    result += `Line Item|${periods.join('|')}|${annualLabels.join('|')}|${ltmLabel}\n`;
  } else {
    // For monthly data: show periods, annual aggregations, YTD, and LTM
    const annualLabels = years.map(y => y.toString());
    result += `Line Item|${periods.join('|')}|${annualLabels.join('|')}|${ytdLabel}|${ltmLabel}\n`;
  }
  Object.entries(report.data).forEach(([lineItem, values]: [string, any]) => {
    const safeLineItem = typeof lineItem === 'string' ? lineItem : String(lineItem ?? '');
    if (typeof values === 'object' && values !== null) {
      // Get values for each period
      const periodValues = periods.map(p => typeof values[p] === 'number' ? formatNumber(values[p]) : '0.00');
      
      // LTM calculation
      const ltmVals = ltmPeriods.map(p => values[p] || 0);
      const ltmValue = formatNumber(ltmVals.reduce((a, b) => a + b, 0));
      
      if (periodType === 'annual') {
        // For annual data: only show periods and LTM
        result += `${safeLineItem.substring(0, 40)}|${periodValues.join('|')}|${ltmValue}\n`;
      } else if (periodType === 'quarterly') {
        // For quarterly data: show periods, annual aggregations, and LTM
        const annualValues = years.map(y => {
          const indices = periods.map((p, idx) => getYear(p) === y ? idx : -1).filter(idx => idx !== -1);
          const vals = indices.map(idx => values[periods[idx]] || 0);
          return formatNumber(vals.reduce((a, b) => a + b, 0));
        });
        result += `${safeLineItem.substring(0, 40)}|${periodValues.join('|')}|${annualValues.join('|')}|${ltmValue}\n`;
      } else {
        // For monthly data: show periods, annual aggregations, YTD, and LTM
        const annualValues = years.map(y => {
          const indices = periods.map((p, idx) => getYear(p) === y ? idx : -1).filter(idx => idx !== -1);
          const vals = indices.map(idx => values[periods[idx]] || 0);
          return formatNumber(vals.reduce((a, b) => a + b, 0));
        });
        const ytdIndices = periods.map((p, idx) => getYear(p) === currentYear ? idx : -1).filter(idx => idx !== -1);
        const ytdVals = ytdIndices.map(idx => values[periods[idx]] || 0);
        const ytdValue = formatNumber(ytdVals.reduce((a, b) => a + b, 0));
        result += `${safeLineItem.substring(0, 40)}|${periodValues.join('|')}|${annualValues.join('|')}|${ytdValue}|${ltmValue}\n`;
      }
    }
  });
  return result;
}
function createPLTable(pnlReport: any, periodType: 'monthly' | 'quarterly' | 'annual' = 'monthly'): string {
  if (!pnlReport || !pnlReport.data) return '';
  return createEnhancedTable(pnlReport, 'P&L', periodType);
}
function createBSTable(bsReport: any, periodType: 'monthly' | 'quarterly' | 'annual' = 'monthly'): string {
  if (!bsReport || !bsReport.data) return '';

  // Use specialized BS table creation to handle balance sheet items correctly (period-end values)
  return createEnhancedBSTable(bsReport, 'Balance Sheet', periodType);
}

// Specialized BS table that uses period-end values for all balance sheet items
function createEnhancedBSTable(report: any, reportType: string, periodType: 'monthly' | 'quarterly' | 'annual' = 'monthly'): string {
  if (!report || !report.data) return '';

  const sortPeriods = (periods: string[]) => {
    return periods
      .filter((p): p is string => typeof p === 'string' && p.includes('-'))
      .sort((a, b) => {
        const parseDate = (period: string) => {
          if (typeof period !== 'string' || !period.includes('-')) return new Date('2000-01-01');
          const [month, year] = period.split('-');
          const fullYear = year && year.length === 2 ? '20' + year : year || '2000';
          return new Date(`${month || 'Jan'} 1, ${fullYear}`);
        };
        return parseDate(a).getTime() - parseDate(b).getTime();
      });
  };

  const firstLineItem = Object.keys(report.data)[0];
  if (!firstLineItem) return '';
  const periods = sortPeriods(Object.keys(report.data[firstLineItem]));
  if (periods.length === 0) return '';
  const years = getAllYears(periods);

  // Period-aware LTM calculation
  let ltmPeriods: string[] = [];
  let ltmLabel = 'LTM';
  if (periodType === 'monthly') {
    ltmPeriods = periods.slice(-12); // Last 12 months
    ltmLabel = ltmPeriods.length > 0 ? `LTM ${ltmPeriods[ltmPeriods.length-1]}` : 'LTM';
  } else if (periodType === 'quarterly') {
    ltmPeriods = periods.slice(-4); // Last 4 quarters = 12 months
    ltmLabel = ltmPeriods.length > 0 ? `LTM ${ltmPeriods[ltmPeriods.length-1]}` : 'LTM';
  } else if (periodType === 'annual') {
    ltmPeriods = periods.slice(-1); // For annual data, LTM = most recent year
    ltmLabel = ltmPeriods.length > 0 ? `LTM ${ltmPeriods[ltmPeriods.length-1]}` : 'LTM';
  }

  // YTD: periods in current year (only relevant for monthly/quarterly)
  const currentYear = getYear(periods[periods.length-1]);
  const ytdPeriods = periods.filter(p => getYear(p) === currentYear);
  const ytdLabel = `YTD ${currentYear}`;

  // Period-aware header construction
  let result = `All amounts in actual USD amounts (except percentages)\n`;
  result += `${reportType.toUpperCase()} DATA (Table Format)\n`;
  result += `Note: Monthly columns are period-end values. Annual/YTD/LTM columns are pre-computed summaries. All values in actual USD.\n`;

  if (periodType === 'annual') {
    // For annual data: only show periods and LTM (no redundant annual aggregation)
    result += `Line Item|${periods.join('|')}|${ltmLabel}\n`;
  } else if (periodType === 'quarterly') {
    // For quarterly data: show periods, annual aggregations, and LTM
    const annualLabels = years.map(y => y.toString());
    result += `Line Item|${periods.join('|')}|${annualLabels.join('|')}|${ltmLabel}\n`;
  } else {
    // For monthly data: show periods, annual aggregations, YTD, and LTM
    const annualLabels = years.map(y => y.toString());
    result += `Line Item|${periods.join('|')}|${annualLabels.join('|')}|${ytdLabel}|${ltmLabel}\n`;
  }

  // Helper function to get period-end value (most recent period in the set)
  const getPeriodEndValue = (periodIndices: number[]): string => {
    if (periodIndices.length === 0) return '0.00';
    // Get the most recent period (highest index)
    const latestIndex = Math.max(...periodIndices);
    const value = Object.values(report.data)[0] as any;
    const periodValue = value[periods[latestIndex]];
    return typeof periodValue === 'number' ? formatNumber(periodValue) : '0.00';
  };

  Object.entries(report.data).forEach(([lineItem, values]: [string, any]) => {
    const safeLineItem = typeof lineItem === 'string' ? lineItem : String(lineItem ?? '');
    if (typeof values === 'object' && values !== null) {
      // Get values for each period
      const periodValues = periods.map(p => typeof values[p] === 'number' ? formatNumber(values[p]) : '0.00');

      // For ALL balance sheet items, use period-end values (spot balances)

      // LTM calculation - use most recent period value
      const ltmIndices = ltmPeriods.map(p => periods.indexOf(p)).filter(idx => idx !== -1);
      const ltmValue = ltmIndices.length > 0 ?
        (typeof values[ltmPeriods[ltmPeriods.length-1]] === 'number' ?
          formatNumber(values[ltmPeriods[ltmPeriods.length-1]]) : '0.00') : '0.00';

      if (periodType === 'annual') {
        result += `${safeLineItem.substring(0, 40)}|${periodValues.join('|')}|${ltmValue}\n`;
      } else if (periodType === 'quarterly') {
        // Annual values - use year-end value (or most recent in year)
        const annualValues = years.map(y => {
          const yearPeriods = periods.filter(p => getYear(p) === y);
          if (yearPeriods.length === 0) return '0.00';
          const latestYearPeriod = yearPeriods[yearPeriods.length - 1];
          return typeof values[latestYearPeriod] === 'number' ? formatNumber(values[latestYearPeriod]) : '0.00';
        });
        result += `${safeLineItem.substring(0, 40)}|${periodValues.join('|')}|${annualValues.join('|')}|${ltmValue}\n`;
      } else {
        // Annual values - use year-end value (or most recent in year)
        const annualValues = years.map(y => {
          const yearPeriods = periods.filter(p => getYear(p) === y);
          if (yearPeriods.length === 0) return '0.00';
          const latestYearPeriod = yearPeriods[yearPeriods.length - 1];
          return typeof values[latestYearPeriod] === 'number' ? formatNumber(values[latestYearPeriod]) : '0.00';
        });

        // YTD value - use most recent period in current year
        const currentYearPeriods = periods.filter(p => getYear(p) === currentYear);
        const ytdValue = currentYearPeriods.length > 0 ?
          (typeof values[currentYearPeriods[currentYearPeriods.length - 1]] === 'number' ?
            formatNumber(values[currentYearPeriods[currentYearPeriods.length - 1]]) : '0.00') : '0.00';

        result += `${safeLineItem.substring(0, 40)}|${periodValues.join('|')}|${annualValues.join('|')}|${ytdValue}|${ltmValue}\n`;
      }
    }
  });
  return result;
}
function createCFTable(cfReport: any, periodType: 'monthly' | 'quarterly' | 'annual' = 'monthly'): string {
  if (!cfReport || !cfReport.data) return '';
  return createEnhancedTable(cfReport, 'Cash Flow', periodType);
}
function createNWCTable(nwcReport: any, periodType: 'monthly' | 'quarterly' | 'annual' = 'monthly'): string {
  if (!nwcReport || !nwcReport.data) return '';

  // Use specialized NWC table creation to handle ratios correctly
  return createEnhancedNWCTable(nwcReport, 'Net Working Capital', periodType);
}

// Specialized NWC table that handles ratios correctly (no summing for DIO/DSO/DPO/CCC)
function createEnhancedNWCTable(report: any, reportType: string, periodType: 'monthly' | 'quarterly' | 'annual' = 'monthly'): string {
  if (!report || !report.data) return '';

  const sortPeriods = (periods: string[]) => {
    return periods
      .filter((p): p is string => typeof p === 'string' && p.includes('-'))
      .sort((a, b) => {
        const parseDate = (period: string) => {
          if (typeof period !== 'string' || !period.includes('-')) return new Date('2000-01-01');
          const [month, year] = period.split('-');
          const fullYear = year && year.length === 2 ? '20' + year : year || '2000';
          return new Date(`${month || 'Jan'} 1, ${fullYear}`);
        };
        return parseDate(a).getTime() - parseDate(b).getTime();
      });
  };

  const firstLineItem = Object.keys(report.data)[0];
  if (!firstLineItem) return '';
  const periods = sortPeriods(Object.keys(report.data[firstLineItem]));
  if (periods.length === 0) return '';
  const years = getAllYears(periods);

  // Period-aware LTM calculation
  let ltmPeriods: string[] = [];
  let ltmLabel = 'LTM';
  if (periodType === 'monthly') {
    ltmPeriods = periods.slice(-12); // Last 12 months
    ltmLabel = ltmPeriods.length > 0 ? `LTM ${ltmPeriods[ltmPeriods.length-1]}` : 'LTM';
  } else if (periodType === 'quarterly') {
    ltmPeriods = periods.slice(-4); // Last 4 quarters = 12 months
    ltmLabel = ltmPeriods.length > 0 ? `LTM ${ltmPeriods[ltmPeriods.length-1]}` : 'LTM';
  } else if (periodType === 'annual') {
    ltmPeriods = periods.slice(-1); // For annual data, LTM = most recent year
    ltmLabel = ltmPeriods.length > 0 ? `LTM ${ltmPeriods[ltmPeriods.length-1]}` : 'LTM';
  }

  // YTD: periods in current year (only relevant for monthly/quarterly)
  const currentYear = getYear(periods[periods.length-1]);
  const ytdPeriods = periods.filter(p => getYear(p) === currentYear);
  const ytdLabel = `YTD ${currentYear}`;

  // Period-aware header construction
  let result = `All amounts in actual USD amounts (except percentages)\n`;
  result += `${reportType.toUpperCase()} DATA (Table Format)\n`;
  result += `Note: Monthly columns are period-end values. Annual/YTD/LTM columns are pre-computed summaries. All values in actual USD.\n`;

  if (periodType === 'annual') {
    // For annual data: only show periods and LTM (no redundant annual aggregation)
    result += `Line Item|${periods.join('|')}|${ltmLabel}\n`;
  } else if (periodType === 'quarterly') {
    // For quarterly data: show periods, annual aggregations, and LTM
    const annualLabels = years.map(y => y.toString());
    result += `Line Item|${periods.join('|')}|${annualLabels.join('|')}|${ltmLabel}\n`;
  } else {
    // For monthly data: show periods, annual aggregations, YTD, and LTM
    const annualLabels = years.map(y => y.toString());
    result += `Line Item|${periods.join('|')}|${annualLabels.join('|')}|${ytdLabel}|${ltmLabel}\n`;
  }

  // Helper function to check if a line item is an NWC ratio
  const isNWCRatio = (lineItem: string): boolean => {
    const nwcRatios = [
      'Days Inventory Outstanding (DIO)',
      'Days Sales Outstanding (DSO)',
      'Days Payable Outstanding (DPO)',
      'Cash Conversion Cycle',
      'Net Working Capital % of Net Revenue'
    ];
    return nwcRatios.some(ratio => lineItem.includes(ratio.replace(' (DIO)', '').replace(' (DSO)', '').replace(' (DPO)', '')));
  };

  Object.entries(report.data).forEach(([lineItem, values]: [string, any]) => {
    const safeLineItem = typeof lineItem === 'string' ? lineItem : String(lineItem ?? '');
    if (typeof values === 'object' && values !== null) {
      // Get values for each period
      const periodValues = periods.map(p => typeof values[p] === 'number' ? formatNumber(values[p]) : '0.00');

      // Check if this is an NWC ratio that shouldn't be summed
      const isRatio = isNWCRatio(safeLineItem);

      if (isRatio) {
        // For NWC ratios, calculate averages for aggregated columns

        // Helper function to calculate average of valid values
        const calculateRatioAverage = (periodIndices: number[]): string => {
          const validValues = periodIndices
            .map(idx => values[periods[idx]])
            .filter(val => val !== null && val !== undefined && !isNaN(val) && val !== 0);

          if (validValues.length === 0) return 'n/a';
          const average = validValues.reduce((sum, val) => sum + val, 0) / validValues.length;
          return formatNumber(average);
        };

        // LTM calculation - average of last 12 months (or available periods)
        const ltmIndices = ltmPeriods.map(p => periods.indexOf(p)).filter(idx => idx !== -1);
        const ltmValue = calculateRatioAverage(ltmIndices);

        if (periodType === 'annual') {
          result += `${safeLineItem.substring(0, 40)}|${periodValues.join('|')}|${ltmValue}\n`;
        } else if (periodType === 'quarterly') {
          // Annual values - average of periods in each year
          const annualValues = years.map(y => {
            const indices = periods.map((p, idx) => getYear(p) === y ? idx : -1).filter(idx => idx !== -1);
            return calculateRatioAverage(indices);
          });
          result += `${safeLineItem.substring(0, 40)}|${periodValues.join('|')}|${annualValues.join('|')}|${ltmValue}\n`;
        } else {
          // Annual values - average of periods in each year
          const annualValues = years.map(y => {
            const indices = periods.map((p, idx) => getYear(p) === y ? idx : -1).filter(idx => idx !== -1);
            return calculateRatioAverage(indices);
          });

          // YTD value - average of periods in current year
          const ytdIndices = periods.map((p, idx) => getYear(p) === currentYear ? idx : -1).filter(idx => idx !== -1);
          const ytdValue = calculateRatioAverage(ytdIndices);

          result += `${safeLineItem.substring(0, 40)}|${periodValues.join('|')}|${annualValues.join('|')}|${ytdValue}|${ltmValue}\n`;
        }
      } else {
        // For regular NWC balance items, use period-end values (spot balances)

        // Helper function to get period-end value (most recent period in the set)
        const getPeriodEndValue = (periodIndices: number[]): string => {
          if (periodIndices.length === 0) return '0.00';
          // Get the most recent period (highest index)
          const latestIndex = Math.max(...periodIndices);
          const value = values[periods[latestIndex]];
          return typeof value === 'number' ? formatNumber(value) : '0.00';
        };

        // LTM calculation - use most recent period value
        const ltmIndices = ltmPeriods.map(p => periods.indexOf(p)).filter(idx => idx !== -1);
        const ltmValue = getPeriodEndValue(ltmIndices);

        if (periodType === 'annual') {
          result += `${safeLineItem.substring(0, 40)}|${periodValues.join('|')}|${ltmValue}\n`;
        } else if (periodType === 'quarterly') {
          // Annual values - use year-end value (or most recent in year)
          const annualValues = years.map(y => {
            const indices = periods.map((p, idx) => getYear(p) === y ? idx : -1).filter(idx => idx !== -1);
            return getPeriodEndValue(indices);
          });
          result += `${safeLineItem.substring(0, 40)}|${periodValues.join('|')}|${annualValues.join('|')}|${ltmValue}\n`;
        } else {
          // Annual values - use year-end value (or most recent in year)
          const annualValues = years.map(y => {
            const indices = periods.map((p, idx) => getYear(p) === y ? idx : -1).filter(idx => idx !== -1);
            return getPeriodEndValue(indices);
          });

          // YTD value - use most recent period in current year
          const ytdIndices = periods.map((p, idx) => getYear(p) === currentYear ? idx : -1).filter(idx => idx !== -1);
          const ytdValue = getPeriodEndValue(ytdIndices);

          result += `${safeLineItem.substring(0, 40)}|${periodValues.join('|')}|${annualValues.join('|')}|${ytdValue}|${ltmValue}\n`;
        }
      }
    }
  });
  return result;
}
function createRatiosTable(ratiosReport: any): string {
  if (!ratiosReport || !ratiosReport.line_items || !Array.isArray(ratiosReport.line_items)) {
    console.log('[createRatiosTable] Invalid ratios report:', ratiosReport);
    return 'No financial ratios data available.';
  }
  
  const sortPeriods = (periods: string[]) => {
    return periods
      .filter((p): p is string => typeof p === 'string' && p.includes('-'))
      .sort((a, b) => {
        const parseDate = (period: string) => {
          if (typeof period !== 'string' || !period.includes('-')) return new Date('2000-01-01');
          const [month, year] = period.split('-');
          const fullYear = year && year.length === 2 ? '20' + year : year || '2000';
          return new Date(`${month || 'Jan'} 1, ${fullYear}`);
        };
        return parseDate(a).getTime() - parseDate(b).getTime();
      });
  };
  
  const periods = ratiosReport.periods ? sortPeriods(ratiosReport.periods) : [];
  if (periods.length === 0) {
    console.log('[createRatiosTable] No periods found:', ratiosReport.periods);
    return 'No periods available for financial ratios.';
  }
  
  console.log('[createRatiosTable] Periods:', periods);
  console.log('[createRatiosTable] Line items count:', ratiosReport.line_items.length);
  
  let result = `All amounts in actual USD amounts (except percentages)\n`;
  result += `FINANCIAL RATIOS DATA (Table Format)\n`;
  result += `Ratio|Formula|${periods.join('|')}\n`;
  
  ratiosReport.line_items.forEach((ratio: any, index: number) => {
    const safeName = typeof ratio.name === 'string' ? ratio.name : String(ratio.name ?? '');
    const safeFormula = typeof ratio.formula === 'string' ? ratio.formula : String(ratio.formula ?? 'N/A');
    
    console.log(`[createRatiosTable] Ratio ${index}: ${safeName}, type: ${ratio.type}, values:`, ratio.values);
    
    if (ratio.type === 'ratio' && Array.isArray(ratio.values)) {
      const periodValues = periods.map((_, index) => {
        const val = ratio.values[index];
        console.log(`[createRatiosTable] Processing value at index ${index}:`, val);
        if (val === null || val === undefined || isNaN(val)) return 'n/a';
        if (ratio.format === 'percentage') {
          return typeof val === 'number' ? formatPercent(val) : 'n/a';
        }
        return typeof val === 'number' ? formatNumber(val) : 'n/a';
      });
      result += `${safeName.substring(0, 35)}|${safeFormula.substring(0, 40)}|${periodValues.join('|')}\n`;
    }
  });
  return result;
}
function transformLineItemsToPeriodMap(report: any) {
  if (!report || !report.data || !Array.isArray(report.data.line_items)) return {};
  const periods = report.data.periods || [];
  const result: Record<string, Record<string, number>> = {};
  report.data.line_items.forEach((item: any) => {
    if (!item.name || !Array.isArray(item.values)) return;
    result[item.name] = {};
    item.values.forEach((val: number, idx: number) => {
      const period = periods[idx];
      if (period) result[item.name][period] = val;
    });
  });
  return result;
}
function transformCFDetailToPeriodMap(report: any) {
  if (!report || !report.data || !Array.isArray(report.data.sections)) return {};
  const periods = report.data.periods || [];
  const result: Record<string, Record<string, number>> = {};
  report.data.sections.forEach((section: any) => {
    if (!Array.isArray(section.line_items)) return;
    section.line_items.forEach((item: any) => {
      if (!item.name || !Array.isArray(item.values)) return;
      result[item.name] = {};
      item.values.forEach((val: number, idx: number) => {
        const period = periods[idx];
        if (period) result[item.name][period] = val;
      });
    });
  });
  return result;
}

// Helper to robustly extract ratios data
function getRatiosData(report: any) {
  console.log('[getRatiosData] Input report:', report);
  if (!report) {
    console.log('[getRatiosData] No report provided');
    return null;
  }
  if (report.data && report.data.periods && report.data.line_items) {
    console.log('[getRatiosData] Returning report.data:', report.data);
    return report.data;
  }
  if (report.periods && report.line_items) {
    console.log('[getRatiosData] Returning report directly:', report);
    return report;
  }
  console.log('[getRatiosData] No valid structure found');
  return null;
}

// --- Quarterly transformation helpers ---
function getQuarterEndMonths(months: string[]): string[] {
  // Returns months that are quarter-ends (Mar, Jun, Sep, Dec)
  return months.filter(m => {
    const mon = m.split('-')[0];
    return ['Mar', 'Jun', 'Sep', 'Dec'].includes(mon);
  });
}

function transformToQuarterly(data: any) {
  if (!data || !Array.isArray(data.periods) || !Array.isArray(data.line_items)) return data;
  const months = data.periods;
  const quarterEnds = getQuarterEndMonths(months);
  const newLineItems = data.line_items.map((item: any) => {
    if (!Array.isArray(item.values)) return item;
    const newValues = quarterEnds.map(qm => {
      const idx = months.indexOf(qm);
      return idx !== -1 ? item.values[idx] : null;
    });
    return { ...item, values: newValues };
  });
  return { periods: quarterEnds, line_items: newLineItems };
}

function transformRatiosToQuarterly(ratios: any) {
  if (!ratios || !Array.isArray(ratios.periods) || !Array.isArray(ratios.line_items)) return ratios;
  const months = ratios.periods;
  const quarterEnds = getQuarterEndMonths(months);
  const newLineItems = ratios.line_items.map((item: any) => {
    if (!Array.isArray(item.values)) return item;
    const newValues = quarterEnds.map(qm => {
      const idx = months.indexOf(qm);
      return idx !== -1 ? item.values[idx] : null;
    });
    return { ...item, values: newValues };
  });
  return { periods: quarterEnds, line_items: newLineItems, sections: ratios.sections };
}

// --- Helper to aggregate monthly data into quarters for PL ---
function groupMonthsByQuarter(months: string[]): Record<string, string[]> {
  // Returns { 'Q1-24': [Jan-24, Feb-24, Mar-24], ... }
  const quarterMap: Record<string, string[]> = {};
  months.forEach(m => {
    const [mon, yr] = m.split('-');
    let q = '';
    if (['Jan', 'Feb', 'Mar'].includes(mon)) q = `Q1-${yr}`;
    else if (['Apr', 'May', 'Jun'].includes(mon)) q = `Q2-${yr}`;
    else if (['Jul', 'Aug', 'Sep'].includes(mon)) q = `Q3-${yr}`;
    else if (['Oct', 'Nov', 'Dec'].includes(mon)) q = `Q4-${yr}`;
    if (!quarterMap[q]) quarterMap[q] = [];
    quarterMap[q].push(m);
  });
  return quarterMap;
}

function aggregatePLToQuarters(data: any): any {
  if (!data || !Array.isArray(data.periods) || !Array.isArray(data.line_items)) return data;
  const months = data.periods;
  const quarterGroups = groupMonthsByQuarter(months);
  const quarterLabels = Object.keys(quarterGroups).filter(q => quarterGroups[q].length > 0);
  const newLineItems = data.line_items.map((item: any) => {
    if (!Array.isArray(item.values)) return item;
    // For margin % calculations, recalc after sum
    const isMargin = item.name && item.name.toLowerCase().includes('margin %');
    const isGrowth = item.name && item.name.toLowerCase().includes('growth');
    let newValues: (number | null)[] = [];
    if (isMargin || isGrowth) {
      // For margin %, recalc using summed numerators/denominators if possible
      // (Assume previous line is numerator, two lines up is denominator)
      // This is a simplification; for more accuracy, use explicit mapping.
      newValues = quarterLabels.map((q, idx) => {
        // Try to find numerator and denominator line items
        if (item.name.toLowerCase().includes('gross margin %')) {
          const grossMargin = data.line_items.find((li: any) => li.name === 'Gross Margin');
          const netRevenue = data.line_items.find((li: any) => li.name === 'Net Revenue');
          if (!grossMargin || !netRevenue) return null;
          const gmSum = quarterGroups[q].reduce((sum, m) => sum + (grossMargin.values[months.indexOf(m)] || 0), 0);
          const nrSum = quarterGroups[q].reduce((sum, m) => sum + (netRevenue.values[months.indexOf(m)] || 0), 0);
          return nrSum !== 0 ? Math.round((gmSum / nrSum) * 1000) / 10 : null;
        }
        if (item.name.toLowerCase().includes('ebitda margin %')) {
          const ebitda = data.line_items.find((li: any) => li.name === 'EBITDA');
          const netRevenue = data.line_items.find((li: any) => li.name === 'Net Revenue');
          if (!ebitda || !netRevenue) return null;
          const ebitdaSum = quarterGroups[q].reduce((sum, m) => sum + (ebitda.values[months.indexOf(m)] || 0), 0);
          const nrSum = quarterGroups[q].reduce((sum, m) => sum + (netRevenue.values[months.indexOf(m)] || 0), 0);
          return nrSum !== 0 ? Math.round((ebitdaSum / nrSum) * 1000) / 10 : null;
        }
        // Default: n/a
        return null;
      });
    } else {
      newValues = quarterLabels.map(q => quarterGroups[q].reduce((sum, m) => sum + (item.values[months.indexOf(m)] || 0), 0));
    }
    return { ...item, values: newValues };
  });
  return { periods: quarterLabels, line_items: newLineItems };
}

// --- Robust Quarterly Table Renderers ---
function createQuarterlyPLTable(data: any): string {
  if (!data || !Array.isArray(data.periods) || !Array.isArray(data.line_items)) return 'No data.';
  let out = 'QUARTERLY P&L DATA (Table Format)\n';
  out += 'LineItem';
  data.periods.forEach((p: string) => { out += '|' + p; });
  out += '\n';
  data.line_items.forEach((item: any) => {
    if (!item.values || !Array.isArray(item.values)) return;
    out += item.name;
    item.values.forEach((v: number | null | undefined) => {
      out += '|' + (v !== null && v !== undefined ? formatNumber(v) : 'n/a');
    });
    out += '\n';
  });
  return out;
}

function createQuarterlyBSTable(data: any): string {
  if (!data || !Array.isArray(data.periods) || !Array.isArray(data.line_items)) return 'No data.';
  let out = 'QUARTERLY BALANCE SHEET DATA (Table Format)\n';
  out += 'LineItem';
  data.periods.forEach((p: string) => { out += '|' + p; });
  out += '\n';
  data.line_items.forEach((item: any) => {
    if (!item.values || !Array.isArray(item.values)) return;
    out += item.name;
    item.values.forEach((v: number | null | undefined) => {
      out += '|' + (v !== null && v !== undefined ? formatNumber(v) : 'n/a');
    });
    out += '\n';
  });
  return out;
}

interface AITablesProps {
  accounts: AccountEntry[];
  selectedProject: Project | null;
  periodType?: 'monthly' | 'quarterly' | 'annual'; // NEW: Period type for table generation
}

export const AITables: React.FC<AITablesProps> = ({ accounts, selectedProject, periodType = 'monthly' }) => {
  const [reports, setReports] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchReports = async () => {
      if (!selectedProject) return;
      setLoading(true);
      setError(null);
      try {
        const allReports = await getAllFinancialReports(selectedProject.id);
        console.log('[AITables] All reports fetched:', allReports);
        setReports(allReports);
      } catch (err) {
        setError('Failed to fetch financial reports.');
      } finally {
        setLoading(false);
      }
    };
    fetchReports();
  }, [selectedProject]);

  if (process.env.NODE_ENV === 'production') return null;
  if (!selectedProject) return <div className="p-4">No project selected.</div>;
  if (loading) return <div className="p-4">Loading AI tables...</div>;
  if (error) return <div className="p-4 text-red-600">{error}</div>;

  // Period-aware data transformations
  const monthlyPL = reports?.pnl_detail?.data;
  const monthlyBS = reports?.bs_detail?.data;
  const quarterlyPL = transformToQuarterly(reports?.pnl_detail?.data);
  const quarterlyBS = transformToQuarterly(reports?.bs_detail?.data);
  const quarterlyPLAggregated = aggregatePLToQuarters(monthlyPL);
  
  // Select appropriate data based on period type
  const getPeriodSpecificData = () => {
    if (periodType === 'quarterly') {
      return {
        plData: quarterlyPLAggregated,
        bsData: quarterlyBS,
        ratiosData: transformRatiosToQuarterly(getRatiosData(reports?.fin_ratios))
      };
    } else if (periodType === 'annual') {
      // For annual, use monthly data but focus on annual aggregations in tables
      return {
        plData: monthlyPL,
        bsData: monthlyBS,
        ratiosData: getRatiosData(reports?.fin_ratios)
      };
    } else {
      // Monthly (default)
      return {
        plData: monthlyPL,
        bsData: monthlyBS,
        ratiosData: getRatiosData(reports?.fin_ratios)
      };
    }
  };

  const { plData, bsData, ratiosData } = getPeriodSpecificData();
  
  console.log(`[AITables] Period Type: ${periodType}`, { plData, bsData, ratiosData });
  console.log(`[AITables] Raw reports structure:`, { reports });

  return (
    <div className="p-4">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-bold">AI Tables (Debug View)</h2>
        <div className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm font-medium">
          {getPeriodTypeDisplayText(periodType)} View
        </div>
      </div>
      <div className="mb-6">
        <h3 className="font-semibold mb-2">Trial Balance Data</h3>
        <pre className="bg-gray-100 p-2 rounded overflow-x-auto text-xs">
          {createCompactTableFormat(accounts)}
        </pre>
      </div>
      <div className="mb-6">
        <h3 className="font-semibold mb-2">{getPeriodTypeDisplayText(periodType)} P&L Statement Data</h3>
        <pre className="bg-gray-100 p-2 rounded overflow-x-auto text-xs">
          {createPLTable(plData ? { data: transformLineItemsToPeriodMap(reports.pnl_detail) } : null, periodType)}
        </pre>
      </div>
      <div className="mb-6">
        <h3 className="font-semibold mb-2">{getPeriodTypeDisplayText(periodType)} Balance Sheet Data</h3>
        <pre className="bg-gray-100 p-2 rounded overflow-x-auto text-xs">
          {createBSTable(reports?.bs_detail ? { data: transformLineItemsToPeriodMap(reports.bs_detail) } : null, periodType)}
        </pre>
      </div>
      <div className="mb-6">
        <h3 className="font-semibold mb-2">{getPeriodTypeDisplayText(periodType)} Cash Flow Data</h3>
        <pre className="bg-gray-100 p-2 rounded overflow-x-auto text-xs">
          {createCFTable(reports?.cf_detail ? { data: transformCFDetailToPeriodMap(reports.cf_detail) } : null, periodType)}
        </pre>
      </div>
      <div className="mb-6">
        <h3 className="font-semibold mb-2">{getPeriodTypeDisplayText(periodType)} Net Working Capital Data</h3>
        <pre className="bg-gray-100 p-2 rounded overflow-x-auto text-xs">
          {createNWCTable(reports?.nwc_detail ? { data: transformLineItemsToPeriodMap(reports.nwc_detail) } : null, periodType)}
        </pre>
      </div>
      <div className="mb-6">
        <h3 className="font-semibold mb-2">{getPeriodTypeDisplayText(periodType)} Financial Ratios Data</h3>
        <pre className="bg-gray-100 p-2 rounded overflow-x-auto text-xs">
          {createRatiosTable(ratiosData)}
        </pre>
      </div>
      
      {/* Period-specific additional sections */}
      {periodType === 'monthly' && (
        <>
          <div className="mt-8 border-t pt-6">
            <h2 className="text-lg font-bold mb-2 text-blue-700">Additional Quarterly Views (Reference)</h2>
            <div className="mb-6">
              <h3 className="font-semibold mb-2">Quarterly P&L Table</h3>
              <pre className="text-xs bg-blue-50 p-2 rounded overflow-x-auto">
                {createQuarterlyPLTable(quarterlyPLAggregated)}
              </pre>
            </div>
            <div className="mb-6">
              <h3 className="font-semibold mb-2">Quarterly Balance Sheet Table</h3>
              <pre className="text-xs bg-blue-50 p-2 rounded overflow-x-auto">
                {createQuarterlyBSTable(quarterlyBS)}
              </pre>
            </div>
          </div>
        </>
      )}
      
      {periodType === 'annual' && (
        <div className="mt-8 border-t pt-6">
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <p className="text-sm text-yellow-800">
              <strong>Annual View:</strong> Tables above emphasize annual aggregations (YTD, LTM, yearly totals) 
              for multi-year trend analysis and long-term performance assessment.
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

// Helper function for period type display text
function getPeriodTypeDisplayText(periodType: 'monthly' | 'quarterly' | 'annual'): string {
  const displayTexts = {
    monthly: 'Monthly',
    quarterly: 'Quarterly', 
    annual: 'Annual'
  };
  return displayTexts[periodType];
} 